// src/App.tsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import "./App.css";
import {
  Layout,
  Input,
  Row,
  Col,
  Card,
  Typography,
  Spin,
  Empty,
  Button,
  Modal,
  Tag,
  Space,
  Badge,
  message,
  Pagination,
  Select,
} from "antd";
import {
  ReadOutlined,
  HeartOutlined,
  HeartFilled,
  BookOutlined,
  ArrowLeftOutlined,
  SearchOutlined, // Added SearchOutlined
} from "@ant-design/icons";
import { OpenLibraryDoc, OpenLibraryResponse, BookDetails } from "./types";

const { Header, Content, Footer } = Layout;
// const { Search } = Input; // Removed Search destructuring as we use Input directly
const { Meta } = Card;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const PAGE_SIZE = 12;
const MAX_API_RESULTS = 12000;

// Popular Categories
const CATEGORIES = [
  "Computer Science",
  "Fiction",
  "History",
  "Science",
  "Business",
  "Psychology",
  "Art",
  "Biography",
];

const App: React.FC = () => {
  // --- STATE ---
  const [loading, setLoading] = useState<boolean>(false);
  const [books, setBooks] = useState<OpenLibraryDoc[]>([]);
  const [favorites, setFavorites] = useState<OpenLibraryDoc[]>([]);

  const [selectedBook, setSelectedBook] = useState<OpenLibraryDoc | null>(null);
  const [descriptionLoading, setDescriptionLoading] = useState<boolean>(false);
  const [currentDescription, setCurrentDescription] = useState<string>("");

  const [showFavorites, setShowFavorites] = useState<boolean>(false);

  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentQuery, setCurrentQuery] = useState<string>("");
  const [sortOption, setSortOption] = useState<string>("relevance");

  // New State for Input Debouncing
  const [searchTerm, setSearchTerm] = useState<string>("");

  useEffect(() => {
    const saved = localStorage.getItem("ol_favorites");
    if (saved) {
      setFavorites(JSON.parse(saved));
    }
  }, []);

  // --- DEBOUNCE EFFECT ---
  // Triggers search 800ms after user stops typing
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const trimmed = searchTerm.trim();
      // Only fetch if length >= 3 OR it's a category search (starts with subject:)
      // This prevents the "Please enter 3 chars" toast from appearing while typing "a", "ab"...
      if (trimmed && (trimmed.length >= 3 || trimmed.startsWith("subject:"))) {
        fetchBooks(trimmed, 1);
      }
    }, 800);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- API LOGIC ---
  const fetchBooks = async (
    query: string,
    page: number = 1,
    sortArg?: string
  ) => {
    const trimmedQuery = query.trim();
    const currentSort = sortArg || sortOption;

    if (!trimmedQuery) {
      // Note: We don't show a warning here during debounce to keep UI clean
      return;
    }
    // Double check length for direct calls (like pagination)
    if (trimmedQuery.length < 3 && !trimmedQuery.startsWith("subject:")) {
      // Only show message if this wasn't a debounce auto-call (optional refinement)
      // For now, the debounce effect logic prevents this case mostly.
      return;
    }

    setLoading(true);
    window.scrollTo({ top: 0, behavior: "smooth" });

    try {
      const sortParam =
        currentSort === "relevance" ? "" : `&sort=${currentSort}`;
      const url = `https://openlibrary.org/search.json?q=${encodeURIComponent(
        trimmedQuery
      )}&page=${page}&limit=${PAGE_SIZE}${sortParam}`;

      const response = await axios.get<OpenLibraryResponse>(url);
      setBooks(response.data.docs || []);
      const realCount = response.data.numFound || 0;
      setTotalItems(Math.min(realCount, MAX_API_RESULTS));
      setCurrentPage(page);
      setCurrentQuery(trimmedQuery);
    } catch (error) {
      console.error("API Error:", error);
      message.error("Failed to load books from Open Library.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDescription = async (workKey: string) => {
    setDescriptionLoading(true);
    setCurrentDescription("");
    try {
      const url = `https://openlibrary.org${workKey}.json`;
      const response = await axios.get<BookDetails>(url);
      const desc = response.data.description;
      if (typeof desc === "string") {
        setCurrentDescription(desc);
      } else if (desc && typeof desc === "object" && desc.value) {
        setCurrentDescription(desc.value);
      } else {
        setCurrentDescription("No description available for this title.");
      }
    } catch (error) {
      console.error("Desc Error:", error);
      setCurrentDescription("Could not load description.");
    } finally {
      setDescriptionLoading(false);
    }
  };

  // --- HANDLERS ---
  const handlePageChange = (page: number) => {
    if (currentQuery) fetchBooks(currentQuery, page);
  };

  const handleSortChange = (value: string) => {
    setSortOption(value);
    if (currentQuery) fetchBooks(currentQuery, 1, value);
  };

  const handleCategorySearch = (category: string) => {
    const query = `subject:${category.toLowerCase()}`;
    setSearchTerm(query); // Update input, let useEffect trigger the fetch
  };

  const handleViewDetails = (book: OpenLibraryDoc) => {
    setSelectedBook(book);
    fetchDescription(book.key);
  };

  const toggleFavorite = (book: OpenLibraryDoc) => {
    let newFavs;
    const exists = favorites.find((f) => f.key === book.key);

    if (exists) {
      newFavs = favorites.filter((f) => f.key !== book.key);
      message.info("Removed from collection");
    } else {
      newFavs = [...favorites, book];
      message.success("Added to collection");
    }
    setFavorites(newFavs);
    localStorage.setItem("ol_favorites", JSON.stringify(newFavs));
  };

  const handleBackToSearch = () => {
    setShowFavorites(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // --- RENDER HELPERS ---
  const getCoverUrl = (coverId?: number, size: "M" | "L" = "M") => {
    return coverId
      ? `https://covers.openlibrary.org/b/id/${coverId}-${size}.jpg`
      : "https://placehold.co/128x196?text=No+Cover";
  };

  const getHighlightedText = (text: string, highlight: string) => {
    if (!highlight.trim()) return text;
    if (highlight.startsWith("subject:")) return text;

    const escaped = highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escaped})`, "gi");
    const parts = text.split(regex);
    return (
      <span>
        {parts.map((part, i) =>
          part.toLowerCase() === highlight.toLowerCase() ? (
            <span
              key={i}
              style={{
                backgroundColor: "#ffecb3",
                color: "#d35400",
                fontWeight: "bold",
                padding: "0 2px",
                borderRadius: "2px",
              }}
            >
              {part}
            </span>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  const displayedBooks = showFavorites ? favorites : books;

  return (
    <Layout className="app-layout">
      {/* HEADER */}
      <Header className="app-header">
        <div className="brand-logo" onClick={handleBackToSearch}>
          <div
            style={{
              background: "#2c3e50",
              borderRadius: "8px",
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BookOutlined style={{ color: "white", fontSize: 20 }} />
          </div>
          <Title
            level={4}
            className="brand-title"
            style={{ color: "#2c3e50", margin: 0, fontWeight: 700 }}
          >
            OpenLibrary<span style={{ color: "#e67e22" }}>Hub</span>
          </Title>
        </div>

        <Badge
          count={favorites.length}
          showZero
          offset={[-5, 5]}
          color="#e67e22"
        >
          <Button
            shape="round"
            size="large"
            type="primary"
            className={`btn-collection ${
              showFavorites
                ? "btn-collection-active"
                : "btn-collection-inactive"
            }`}
            icon={showFavorites ? <HeartFilled /> : <HeartOutlined />}
            onClick={() => setShowFavorites(true)}
          >
            <span className="btn-text">My Collection</span>
          </Button>
        </Badge>
      </Header>

      <Content
        className="app-content"
        style={{
          padding: "40px 24px",
          maxWidth: 1200,
          margin: "0 auto",
          width: "100%",
        }}
      >
        {/* HERO SEARCH SECTION */}
        {!showFavorites && (
          <div className="hero-card">
            <div style={{ maxWidth: 750, margin: "0 auto" }}>
              <Title level={1} style={{ color: "#2c3e50", marginBottom: 10 }}>
                Explore the World's Wisdom
              </Title>
              <Paragraph
                style={{ fontSize: 16, color: "#636e72", marginBottom: 35 }}
              >
                Access millions of books, articles, and texts. Your academic
                journey starts here.
              </Paragraph>

              {/* SEARCH & SORT BAR */}
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Input
                  placeholder="What are you looking for today?"
                  allowClear
                  autoFocus
                  // Icon on left
                  prefix={
                    <SearchOutlined
                      style={{ color: "#b0b0b0", fontSize: 20 }}
                    />
                  }
                  size="large"
                  // Bind to state
                  value={searchTerm}
                  // OnChange event
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    flex: 1,
                    minWidth: "220px",
                    height: 54,
                    borderRadius: 12,
                    // Basic border to match theme (AntD default is fine, but ensuring consistency)
                  }}
                />

                <Select
                  defaultValue="relevance"
                  value={sortOption}
                  size="large"
                  style={{
                    width: 180,
                    flexShrink: 0,
                    maxWidth: "100%",
                    height: 54,
                    borderRadius: 12,
                  }}
                  onChange={handleSortChange}
                  dropdownStyle={{ padding: 10, borderRadius: 12 }}
                >
                  <Option value="relevance">Relevance</Option>
                  <Option value="new">Newest First</Option>
                  <Option value="old">Oldest First</Option>
                </Select>
              </div>

              {/* CATEGORIES ROW */}
              <div
                style={{
                  marginTop: 30,
                  display: "flex",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                <Text type="secondary" style={{ marginRight: 8 }}>
                  Browse Subjects:
                </Text>
                {CATEGORIES.map((cat) => (
                  <Button
                    key={cat}
                    size="small"
                    shape="round"
                    onClick={() => handleCategorySearch(cat)}
                    style={{
                      borderColor: "#e0e0e0",
                      color: "#636e72",
                      fontSize: 13,
                    }}
                    className="category-chip"
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* FAVORITES VIEW HEADER */}
        {showFavorites && (
          <div style={{ marginBottom: 40, textAlign: "center" }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleBackToSearch}
              style={{ fontSize: 16, marginBottom: 10, color: "#636e72" }}
            >
              Back to Search
            </Button>
            <Title level={2} style={{ color: "#2c3e50", marginTop: 0 }}>
              My Personal Collection ({favorites.length})
            </Title>
          </div>
        )}

        {/* BOOKS GRID */}
        <Spin
          spinning={loading && !showFavorites}
          size="large"
          tip="Scanning the shelves..."
        >
          {displayedBooks.length > 0 ? (
            <>
              {currentQuery.startsWith("subject:") && !showFavorites && (
                <div style={{ marginBottom: 20 }}>
                  <Title level={4} style={{ color: "#636e72" }}>
                    Showing results for subject:{" "}
                    <span style={{ color: "#e67e22" }}>
                      {currentQuery.replace("subject:", "")}
                    </span>
                  </Title>
                </div>
              )}

              {/* Responsive Grid Gutter */}
              <Row
                gutter={[
                  { xs: 16, sm: 24, md: 32 },
                  { xs: 16, sm: 24, md: 32 },
                ]}
              >
                {displayedBooks.map((book) => {
                  const isFav = favorites.some((f) => f.key === book.key);
                  const title = book.title || "Unknown Title";

                  return (
                    <Col xs={24} sm={12} md={8} lg={6} key={book.key}>
                      <Card
                        hoverable
                        className="book-card"
                        cover={
                          <div className="book-cover-container">
                            <img
                              alt={title}
                              src={getCoverUrl(book.cover_i, "M")}
                              className="book-cover"
                            />
                          </div>
                        }
                        actions={[
                          <Button
                            type="text"
                            icon={<ReadOutlined />}
                            onClick={() => handleViewDetails(book)}
                          >
                            Details
                          </Button>,
                          <Button
                            type="text"
                            icon={
                              isFav ? (
                                <HeartFilled style={{ color: "#e74c3c" }} />
                              ) : (
                                <HeartOutlined />
                              )
                            }
                            onClick={() => toggleFavorite(book)}
                          />,
                        ]}
                      >
                        <Meta
                          title={
                            <div
                              style={{
                                fontSize: 16,
                                fontWeight: 700,
                                color: "#2c3e50",
                              }}
                              title={title}
                            >
                              {showFavorites
                                ? title
                                : getHighlightedText(title, currentQuery)}
                            </div>
                          }
                          description={
                            <>
                              <Text
                                type="secondary"
                                style={{
                                  display: "block",
                                  marginBottom: 8,
                                  fontSize: 13,
                                }}
                              >
                                {book.author_name?.slice(0, 1).join(", ") ||
                                  "Unknown Author"}
                              </Text>
                              <div style={{ height: 24, overflow: "hidden" }}>
                                {book.first_publish_year && (
                                  <Tag color="cyan">
                                    {book.first_publish_year}
                                  </Tag>
                                )}
                                {book.subject?.slice(0, 1).map((s) => (
                                  <Tag
                                    key={s}
                                    color="orange"
                                    style={{ cursor: "pointer" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCategorySearch(s);
                                    }}
                                    title={`Explore ${s}`}
                                  >
                                    {s}
                                  </Tag>
                                ))}
                              </div>
                            </>
                          }
                        />
                      </Card>
                    </Col>
                  );
                })}
              </Row>

              {!showFavorites && (
                <div
                  style={{
                    marginTop: 60,
                    textAlign: "center",
                    paddingBottom: 40,
                  }}
                >
                  <Pagination
                    current={currentPage}
                    total={totalItems}
                    pageSize={PAGE_SIZE}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                  />
                </div>
              )}
            </>
          ) : (
            !loading && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span style={{ color: "#95a5a6", fontSize: 16 }}>
                    {showFavorites
                      ? "Your collection is currently empty."
                      : "Type a keyword above to discover new books."}
                  </span>
                }
              >
                {showFavorites && (
                  <Button
                    type="primary"
                    onClick={handleBackToSearch}
                    className="btn-collection btn-collection-active"
                  >
                    Find Books
                  </Button>
                )}
              </Empty>
            )
          )}
        </Spin>
      </Content>

      <Footer
        style={{
          textAlign: "center",
          background: "transparent",
          color: "#95a5a6",
          padding: "30px 0",
        }}
      >
        <Space direction="vertical" size={2}>
          <Text type="secondary">
            OpenLibrary Hub © {new Date().getFullYear()}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Powered by Open Library API • Designed for Students & Researchers
          </Text>
        </Space>
      </Footer>

      {/* DETAILS MODAL - RESPONSIVE FIX */}
      <Modal
        open={!!selectedBook}
        onCancel={() => setSelectedBook(null)}
        footer={null}
        style={{ maxWidth: "95vw", width: 750 }}
        centered
        title={null}
        styles={{ mask: { backdropFilter: "blur(4px)" } }}
      >
        {selectedBook && (
          <Row gutter={[32, 32]}>
            <Col xs={24} sm={10} style={{ textAlign: "center", padding: 25 }}>
              <div
                style={{
                  background: "#fdfcf8",
                  padding: 20,
                  borderRadius: 12,
                  border: "1px solid #eee",
                }}
              >
                <img
                  src={getCoverUrl(selectedBook.cover_i, "L")}
                  alt="Cover"
                  style={{
                    width: "100%",
                    borderRadius: 8,
                    boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                  }}
                />
              </div>
              <Button
                type="primary"
                href={`https://openlibrary.org${selectedBook.key}`}
                target="_blank"
                block
                size="large"
                style={{ marginTop: 20, background: "#2c3e50" }}
              >
                View Full Entry
              </Button>
            </Col>
            <Col xs={24} sm={14} style={{ padding: 25 }}>
              <Title level={3} style={{ color: "#2c3e50", marginTop: 0 }}>
                {selectedBook.title}
              </Title>
              <Text style={{ fontSize: 18, color: "#7f8c8d" }}>
                by{" "}
                <strong style={{ color: "#2c3e50" }}>
                  {selectedBook.author_name?.join(", ") || "Unknown"}
                </strong>
              </Text>
              <div style={{ margin: "20px 0" }}>
                <Space wrap>
                  {selectedBook.first_publish_year && (
                    <Tag
                      color="geekblue"
                      style={{ fontSize: 14, padding: "4px 10px" }}
                    >
                      {selectedBook.first_publish_year}
                    </Tag>
                  )}
                  {selectedBook.subject?.slice(0, 5).map((s) => (
                    <Tag
                      key={s}
                      style={{
                        fontSize: 14,
                        padding: "4px 10px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setSelectedBook(null);
                        handleCategorySearch(s);
                      }}
                    >
                      {s}
                    </Tag>
                  ))}
                </Space>
              </div>
              <Title level={5} style={{ color: "#2c3e50" }}>
                Abstract / Summary
              </Title>
              <Spin spinning={descriptionLoading}>
                <div
                  style={{
                    maxHeight: 300,
                    overflowY: "auto",
                    background: "#f8f9fa",
                    padding: 20,
                    borderRadius: 12,
                    lineHeight: "1.6",
                    color: "#2d3436",
                    border: "1px solid #e9ecef",
                  }}
                >
                  {currentDescription || (
                    <Text type="secondary">
                      No summary available for this edition.
                    </Text>
                  )}
                </div>
              </Spin>
            </Col>
          </Row>
        )}
      </Modal>
    </Layout>
  );
};

export default App;
